import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useRequestsList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refUserRequestsTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'id',
      sortable: true,
    },
    {
      key: 'detail',
      label: 'Request details',
      sortable: false,
    },
    {
      key: 'createdBy',
      label: 'created by',
      sortable: true,
    },
    {
      key: 'createdAt',
      label: 'created on',
      sortable: true,
    },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalRequests = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc ? (route.query.sortDesc !== 'false') : true)
  const createdAtFilter = ref(route.query.createdAt ?? null)
  const createdByFilter = ref([])
  if (Array.isArray(route.query.createdBy)) {
    createdByFilter.value = route.query.createdBy
  } else if (route.query.createdBy) {
    createdByFilter.value = [route.query.createdBy]
  }
  const statusFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }
  const currentDataLength = ref(0)
  const requestData = ref([])
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refUserRequestsTable.value ? refUserRequestsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalRequests.value,
    }
  })

  const refetchData = () => {
    refUserRequestsTable.value.refresh()
  }

  const fetchRequests = () => {
    store
      .dispatch('app-user-requests/fetchRequests', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        createdAt: createdAtFilter.value,
        createdBy: createdByFilter.value,
      },
      { root: true })
      .then(response => {
        const { requests, total } = response.data
        requestData.value = requests
        currentDataLength.value = requests.length
        // callback(requests)
        totalRequests.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              createdAt: createdAtFilter.value,
              createdBy: createdByFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  let timer = null
  watch([statusFilter, createdAtFilter, createdByFilter], () => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      currentPage.value = 1
      fetchRequests()
    }, 1000)
  }, { deep: true })

  watch([searchQuery], (current, prev) => {
    const currentVal = current[0]
    const prevVal = prev[0]
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      if ((!currentVal && prevVal) || (currentVal && currentVal.length > 2)) {
        currentPage.value = 1
        fetchRequests()
      }
    }, 1000)
  }, { deep: true })

  watch([sortBy, perPage, isSortDirDesc, currentPage], () => {
    fetchRequests()
  }, { deep: true })

  fetchRequests()

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveRequestStatusVariant = status => {
    if (status === 'approved') return 'success'
    if (status === 'rejected') return 'danger'
    if (status === 'action required') return 'info'
    if (status === 'awaiting approval') return 'warning'
    return 'primary'
  }

  return {
    requestData,
    fetchRequests,
    tableColumns,
    perPage,
    currentPage,
    totalRequests,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserRequestsTable,

    resolveRequestStatusVariant,
    refetchData,
    currentDataLength,

    // Extra Filters
    statusFilter,
    createdAtFilter,
    createdByFilter,
  }
}
